package kangaroorewards.appsdk.core.network

import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive

/**
 * Holds both message and optional description extracted from an error response.
 */
data class ErrorDetails(
    val message: String,
    val description: String? = null
)

/**
 * Extract a human friendly error message and description from a JSON response body.
 *
 * Behavior:
 * - If the top-level object has an "error" object, extract "message" and "description" from it.
 * - Otherwise, iterate the object's keys and collect values (arrays / primitives / nested objects),
 *   return only the values (no keys) joined by new lines as the message.
 * - If parsing fails or nothing useful is found, return the raw responseBody as fallback message.
 */
fun extractErrorMessage(responseBody: String): ErrorDetails {
    val jsonElement = try {
        Json.parseToJsonElement(responseBody)
    } catch (_: Exception) {
        // Not valid JSON — return raw body so caller still sees something.
        return ErrorDetails(message = responseBody)
    }

    if (jsonElement is JsonObject) {
        // Case: {"error": {"message":"...", "description":"..."}}
        jsonElement["error"]?.let { err ->
            when (err) {
                is JsonObject -> {
                    var message: String? = null
                    var description: String? = null

                    // Extract message
                    err["message"]?.let { m ->
                        if (m is JsonPrimitive) message = m.content
                    }

                    // Extract description
                    err["description"]?.let { d ->
                        if (d is JsonPrimitive) description = d.content
                    }

                    // Create immutable copies for smart cast
                    val finalMessage = message
                    val finalDescription = description

                    // If we found a message, return it with description
                    if (finalMessage != null) {
                        return ErrorDetails(message = finalMessage, description = finalDescription)
                    }

                    // If "error" object contains other primitives, return the first primitive string found
                    for ((_, v) in err) {
                        when (v) {
                            is JsonPrimitive -> return ErrorDetails(message = v.content)
                            is JsonArray, is JsonObject -> {
                                val nested = collectMessages(v)
                                if (nested.isNotEmpty()) {
                                    return ErrorDetails(message = nested.joinToString("\n"))
                                }
                            }
                        }
                    }
                }
                is JsonPrimitive -> {
                    val errorDescription = (jsonElement["error_description"] as? JsonPrimitive)?.content
                    return ErrorDetails(message = err.content, description = errorDescription)
                }
                is JsonArray -> {
                    val arr = collectMessages(err)
                    if (arr.isNotEmpty()) {
                        return ErrorDetails(message = arr.joinToString("\n"))
                    }
                }
            }
        }

        // Case: {"first_name":["..."], "last_name":["..."]}
        // Collect only the values (no keys) across all fields and return them joined by new line.
        val allMessages = mutableListOf<String>()
        for ((_, value) in jsonElement) {
            val parts = collectMessages(value)
            if (parts.isNotEmpty()) allMessages.addAll(parts)
        }
        if (allMessages.isNotEmpty()) {
            return ErrorDetails(message = allMessages.joinToString("\n"))
        }
    }

    // fallback to returning the raw body
    return ErrorDetails(message = responseBody)
}

private fun collectMessages(element: JsonElement): List<String> = when (element) {
    is JsonArray -> element.flatMap { collectMessages(it) }
    is JsonObject -> {
        // if it has a "message" primitive, return it first
        element["message"]?.let { m ->
            if (m is JsonPrimitive) return listOf(m.content)
        }
        // otherwise collect recursively from children
        element.flatMap { (_, v) -> collectMessages(v) }
    }
    is JsonPrimitive -> listOf(element.content)
    else -> emptyList()
}